import { supabase } from "./supabase";

// Cache implementation for performance
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Cached data for ${key}`);
};

/**
 * Get system-wide daily performance data from the database
 * This replaces the fake data simulation in Dashboard.js
 */
export const getSystemDailyPerformance = async (
  daysBack = 30,
  brandFilter = null
) => {
  try {
    // Check cache first
    const cacheKey = `system_daily_${daysBack}_${brandFilter || "all"}`;
    const cachedResult = getCachedData(cacheKey);
    if (cachedResult) return cachedResult;

    console.log(
      `🚀 Loading real system daily performance for ${daysBack} days, brand: ${brandFilter || "all"}`
    );

    // Call the database function for real daily data
    const { data, error } = await supabase.rpc("get_system_daily_performance", {
      days_back: daysBack,
      brand_filter: brandFilter,
    });

    if (error) {
      console.error("Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    // Transform the data for frontend consumption
    const transformedData = data.map((day) => ({
      date: day.date,
      spend: parseFloat(day.daily_spend || 0),
      revenue: parseFloat(day.daily_revenue || 0),
      commission: parseFloat(day.daily_commission || 0),
      purchases: parseInt(day.daily_purchases || 0),
      adCount: parseInt(day.daily_ad_count || 0),
      roas: parseFloat(day.daily_roas || 0),
    }));

    console.log(`✅ Real system daily performance loaded:`, {
      days: transformedData.length,
      totalSpend: transformedData.reduce((sum, day) => sum + day.spend, 0),
      totalRevenue: transformedData.reduce((sum, day) => sum + day.revenue, 0),
      totalCommission: transformedData.reduce(
        (sum, day) => sum + day.commission,
        0
      ),
      sample: transformedData.slice(-3), // Show last 3 days as sample
    });

    // Cache the result
    setCachedData(cacheKey, transformedData);

    return transformedData;
  } catch (err) {
    console.error("❌ Error loading system daily performance:", err);

    // Return empty array instead of fake data
    return [];
  }
};

/**
 * Get system-wide performance using for a given date range or All Time
 */
export const getSystemAllTimePerformance = async (
  brandFilter = null,
  onProgress = null,
  startDate = null,
  endDate = null
) => {
  try {
    // Create cache key based on parameters
    const cacheKey =
      startDate && endDate
        ? `system_range_${startDate}_${endDate}_${brandFilter || "all"}`
        : `system_alltime_${brandFilter || "all"}`;

    const cachedResult = getCachedData(cacheKey);
    if (cachedResult) {
      console.log("📋 Using cached data");
      return cachedResult;
    }

    console.log(
      `🚀 Loading system performance data using RPC, brand: ${brandFilter || "all"}, range: ${startDate || "all"} to ${endDate || "all"}`
    );

    // Report loading start
    if (onProgress) {
      onProgress({
        status: "loading",
      });
    }

    let transformedData = [];

    // Determine date range - if not provided, use "All Time" (10 years ago to today)
    let actualStartDate = startDate;
    let actualEndDate = endDate;

    if (!startDate || !endDate) {
      // For "All Time", set start date to 10 years ago and end date to today
      const today = new Date();
      const tenYearsAgo = new Date();
      tenYearsAgo.setFullYear(today.getFullYear() - 10);

      actualStartDate = tenYearsAgo.toISOString().split("T")[0];
      actualEndDate = today.toISOString().split("T")[0];

      console.log(
        `📊 All Time converted to date range: ${actualStartDate} to ${actualEndDate}`
      );
    }

    // Use the unified date range RPC function for all cases
    console.log(
      `📊 Using unified date range RPC function for ${actualStartDate} to ${actualEndDate}...`
    );

    // Use the date range RPC function for all cases
    const { data, error } = await supabase.rpc(
      "get_system_daily_performance",
      {
        start_date: actualStartDate,
        end_date: actualEndDate,
        brand_filter: brandFilter,
      }
    );

    if (error) {
      console.error("❌ Date range RPC error:", error);
      throw new Error(`Date range RPC error: ${error.message}`);
    }

    // Transform the RPC response
    transformedData = (data || []).map((day) => ({
      date: day.date,
      spend: parseFloat(day.daily_spend || 0),
      revenue: parseFloat(day.daily_revenue || 0),
      commission: parseFloat(day.daily_commission || 0),
      purchases: parseInt(day.daily_purchases || 0),
      adCount: parseInt(day.daily_ad_count || 0),
      roas: parseFloat(day.daily_roas || 0),
    }));

    console.log(
      `✅ Loaded ${transformedData.length} days via unified date range RPC for ${actualStartDate} to ${actualEndDate}`
    );

    if (transformedData.length === 0) {
      console.log("⚠️ No data found");
      return [];
    }

    console.log(`✅ System performance loaded via RPC:`, {
      days: transformedData.length,
      dateRange:
        transformedData.length > 0
          ? `${transformedData[0].date} to ${transformedData[transformedData.length - 1].date}`
          : "No data",
      totalSpend: transformedData.reduce((sum, day) => sum + day.spend, 0),
      totalRevenue: transformedData.reduce((sum, day) => sum + day.revenue, 0),
      totalCommission: transformedData.reduce(
        (sum, day) => sum + day.commission,
        0
      ),
    });

    // Report completion
    if (onProgress) {
      onProgress({
        status: "completed",
      });
    }

    // Cache the result
    setCachedData(cacheKey, transformedData);

    return transformedData;
  } catch (err) {
    console.error("❌ Error loading system performance via RPC:", err);

    // Report error status
    if (onProgress) {
      onProgress({
        status: "error",
      });
    }

    return [];
  }
};

// Cache management
/**
 * Get system-wide daily performance data by specific date range
 * This function now just calls getSystemAllTimePerformance with the date range
 * @deprecated Use getSystemAllTimePerformance directly instead
 */
export const getSystemDailyPerformanceByDateRange = async (
  startDate,
  endDate,
  brandFilter = null
) => {
  // Just call the main function with the date range
  return await getSystemAllTimePerformance(
    brandFilter,
    null,
    startDate,
    endDate
  );
};

export const clearSystemPerformanceCache = () => {
  cache.clear();
  console.log("🧹 System performance cache cleared");
};
